import React, { useMemo } from 'react';
import useAppSelector from '../app/redux/useAppSelector';
import type {
  EntitySearchTopicResult,
  EntitySearchUserResult,
  EntitySelectConfig,
} from '../search/entitySearch/EntitySelect';
import EntitySelect from '../search/entitySearch/EntitySelect';
import topicPolicyCheck, { TopicPolicyAction } from '../topic/topicPolicyCheck';
import { useAllTopicMap } from '../topic/useAllTopics';
import { useAllUsersMap } from '../user/useAllUsers';
import {
  topicToEntity,
  userToEntity,
} from '../search/entitySearch/localEntitySearch';

type TemplateOwnerEntityType = 'user' | 'topic';

export type TemplateOwnerEntity = {
  entityType: TemplateOwnerEntityType;
  id: string;
  name: string;
};

interface TemplateOwnerSelectProps {
  autoFocus?: boolean;
  onSelect: (newOwner: TemplateOwnerEntity) => void;
  templateOwner: TemplateOwnerEntity;
}

const useEntityValue = (
  templateOwner: TemplateOwnerEntity,
): EntitySearchUserResult | EntitySearchTopicResult | null => {
  const topicsMap = useAllTopicMap();
  const usersMap = useAllUsersMap();

  if (!templateOwner) {
    return null;
  }

  if (templateOwner.entityType === 'user') {
    const user = usersMap[templateOwner.id];
    return user ? userToEntity(user) : null;
  }
  if (templateOwner.entityType === 'topic') {
    const topic = topicsMap[templateOwner.id];
    return topic ? topicToEntity(topic) : null;
  }

  return null;
};

const TemplateOwnerSelect = ({
  templateOwner,
  onSelect,
  autoFocus,
}: TemplateOwnerSelectProps) => {
  const user = useAppSelector((state) => state.session.user);
  const config = useMemo<EntitySelectConfig>(
    () => ({
      topic: {
        extra: (topic) => {
          // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
          const disabled = !topicPolicyCheck(
            TopicPolicyAction.EditPost,
            topic,
            user,
          );

          return {
            disabled,
            isDisabled: disabled,
          };
        },
      },
      user: {
        filter: (u) => u.id === user.id,
      },
    }),
    [user],
  );
  const value = useEntityValue(templateOwner);

  return (
    <EntitySelect
      styles={{
        menu: (base) => ({ ...base, minWidth: 220 }),
      }}
      config={config}
      onChange={(entity) => {
        if (entity?.type === 'topic' || entity?.type === 'user') {
          onSelect({
            entityType: entity.type,
            id: entity.id,
            name: entity.name,
          });
        }
      }}
      autoFocus={autoFocus}
      value={value}
      blurInputOnSelect
      closeMenuOnSelect
    />
  );
};

export default TemplateOwnerSelect;
